if (!customElements.get('product-sticky-atc')) {
  customElements.define(
    'product-sticky-atc',
    class ProductStickyAtc extends HTMLElement {
      connectedCallback() {
        this.bodyVisibleClass = 'sticky-atc-visible';
        this.sectionId = this.dataset.section;
        this.submitButton = this.querySelector('.product-sticky-atc__submit');
        this.priceContainer = this.querySelector('[data-sticky-price]');
        this.deliveryText = this.querySelector('[data-sticky-delivery]');
        this.sourceButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.syncFromMainSubmitButton();
        this.syncPrice();
        this.syncDeliveryEstimate();
        this.syncViewportOffset = this.syncViewportOffset.bind(this);
        this.setupVisibilityObserver();
        this.handleResize = this.handleResize.bind(this);
        window.addEventListener('resize', this.handleResize);
        window.visualViewport?.addEventListener('resize', this.syncViewportOffset);
        window.visualViewport?.addEventListener('scroll', this.syncViewportOffset);
        this.syncViewportOffset();
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange.bind(this));
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.visibilityObserver?.disconnect();
        window.removeEventListener('resize', this.handleResize);
        window.visualViewport?.removeEventListener('resize', this.syncViewportOffset);
        window.visualViewport?.removeEventListener('scroll', this.syncViewportOffset);
        this.style.removeProperty('--sticky-browser-offset');
        document.body.classList.remove(this.bodyVisibleClass);
      }

      handleVariantChange({ data }) {
        if (!data || data.sectionId !== this.sectionId) return;

        const { variant, html } = data;
        const sourceButton = html?.getElementById(`ProductSubmitButton-${this.sectionId}`);
        const sourcePrice = html?.getElementById(`price-${this.sectionId}`);

        if (sourceButton && this.submitButton) {
          const sourceText = sourceButton.querySelector('span')?.textContent?.trim();
          if (sourceText) this.submitButton.querySelector('span').textContent = sourceText;

          if (sourceButton.hasAttribute('disabled')) {
            this.submitButton.setAttribute('disabled', 'disabled');
          } else {
            this.submitButton.removeAttribute('disabled');
          }
        }

        if (sourcePrice && this.priceContainer) this.priceContainer.innerHTML = sourcePrice.innerHTML;
        if (variant) this.syncDeliveryEstimate();
      }

      syncFromMainSubmitButton() {
        const sourceButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!sourceButton || !this.submitButton) return;

        const sourceText = sourceButton.querySelector('span')?.textContent?.trim();
        if (sourceText) this.submitButton.querySelector('span').textContent = sourceText;

        if (sourceButton.hasAttribute('disabled')) {
          this.submitButton.setAttribute('disabled', 'disabled');
        } else {
          this.submitButton.removeAttribute('disabled');
        }
      }

      syncPrice() {
        const sourcePrice = document.getElementById(`price-${this.sectionId}`);
        if (sourcePrice && this.priceContainer) this.priceContainer.innerHTML = sourcePrice.innerHTML;
      }

      syncDeliveryEstimate() {
        if (!this.deliveryText) return;

        const warehouseTimeZone = 'Europe/London';
        const warehouseFormatter = new Intl.DateTimeFormat('en-US', { month: 'short', timeZone: 'UTC' });
        const warehousePartsFormatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: warehouseTimeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false,
        });
        const getOrdinal = (day) => {
          const remainder = day % 100;
          if (remainder >= 11 && remainder <= 13) return 'th';
          switch (day % 10) {
            case 1:
              return 'st';
            case 2:
              return 'nd';
            case 3:
              return 'rd';
            default:
              return 'th';
          }
        };
        const formatDate = (date) => {
          const day = date.getUTCDate();
          return `${warehouseFormatter.format(date)} ${day}${getOrdinal(day)}`;
        };
        const addDays = (date, days) => {
          const nextDate = new Date(date);
          nextDate.setUTCDate(nextDate.getUTCDate() + days);
          return nextDate;
        };

        const cutoffHour = Number.parseInt(this.dataset.cutoffHour || '16', 10);
        const cutoffMinute = Number.parseInt(this.dataset.cutoffMinute || '45', 10);
        const minDeliveryDays = Number.parseInt(this.dataset.minDeliveryDays || '1', 10);
        const maxDeliveryDays = Number.parseInt(this.dataset.maxDeliveryDays || '3', 10);
        const parts = warehousePartsFormatter.formatToParts(new Date());
        const values = {};
        parts.forEach((part) => {
          if (part.type !== 'literal') values[part.type] = Number.parseInt(part.value, 10);
        });

        const orderDate = new Date(Date.UTC(values.year, values.month - 1, values.day, 12, 0, 0, 0));
        const isAfterCutoff =
          values.hour > cutoffHour ||
          (values.hour === cutoffHour && values.minute >= cutoffMinute);
        const processingStartDate = isAfterCutoff ? addDays(orderDate, 1) : orderDate;
        const startDate = addDays(processingStartDate, minDeliveryDays);
        const endDate = addDays(processingStartDate, Math.max(minDeliveryDays, maxDeliveryDays));

        this.deliveryText.textContent = `Get it between ${formatDate(startDate)} - ${formatDate(endDate)}`;
      }

      setupVisibilityObserver() {
        if (!this.sourceButton) {
          this.classList.remove('is-visible');
          document.body.classList.remove(this.bodyVisibleClass);
          return;
        }

        this.visibilityObserver?.disconnect();

        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            const isVisible = !entry.isIntersecting;
            this.classList.toggle('is-visible', isVisible);
            document.body.classList.toggle(this.bodyVisibleClass, isVisible);
          },
          {
            rootMargin: '0px 0px -20% 0px',
            threshold: 0.15,
          }
        );

        this.visibilityObserver.observe(this.sourceButton);
      }

      handleResize() {
        this.setupVisibilityObserver();
        this.syncViewportOffset();
      }

      syncViewportOffset() {
        const viewport = window.visualViewport;
        if (!viewport) {
          this.style.removeProperty('--sticky-browser-offset');
          return;
        }

        const browserOffset = Math.max(0, window.innerHeight - (viewport.height + viewport.offsetTop));
        this.style.setProperty('--sticky-browser-offset', `${browserOffset}px`);
      }
    }
  );
}
